import React, { useState, useEffect, useContext, useMemo } from 'react';
import { LocalizationContext } from '../localization/LocalizationContext';
import { smartTranslateIngredients } from '../localization/foodTranslations';
import { parseMultiLanguageDescription, hasMultiLanguageFormat } from '../utils/multiLanguageParser';
import '../css/modifyers.css';

const ModifierModal = ({ product, onClose, onConfirm, disabledModifierIds = new Set() }) => {
    const { translations, locale } = useContext(LocalizationContext);
    const size = product.itemSizes?.[0];
    const allModifierGroups = size?.itemModifierGroups || [];
    const modifierGroups = useMemo(() => allModifierGroups
        .map(group => ({
            ...group,
            items: group.items.filter(item => !disabledModifierIds.has(item.itemId)),
        }))
        .filter(group => group.items.length > 0), [allModifierGroups, disabledModifierIds]);
    const [selectedModifiers, setSelectedModifiers] = useState([]);
    const [groupCounts, setGroupCounts] = useState({});

    const [isAddDisabled, setIsAddDisabled] = useState(true);

    const isMandatoryGroup = (group) => {
        const name = (group.name || '').toLowerCase();
        return name.includes('pizza box');
    };

    useEffect(() => {
        const initialCounts = {};
        const mandatoryModifiers = [];
        modifierGroups.forEach(group => {
            if (isMandatoryGroup(group)) {
                group.items.forEach(item => {
                    mandatoryModifiers.push({
                        productId: item.itemId,
                        amount: 1,
                        productGroupId: group.itemGroupId,
                        price: item.prices?.[0]?.price || 0,
                    });
                });
                initialCounts[group.itemGroupId] = group.items.length;
            } else {
                initialCounts[group.itemGroupId] = 0;
            }
        });
        setGroupCounts(initialCounts);
        if (mandatoryModifiers.length > 0) {
            setSelectedModifiers(mandatoryModifiers);
        }
    }, [modifierGroups]);

    useEffect(() => {
        const allSatisfied = modifierGroups.every(group => {
            const count = groupCounts[group.itemGroupId] || 0;
            const effectiveMin = Math.min(group.restrictions.minQuantity, group.items.length);
            return count >= effectiveMin;
        });
        setIsAddDisabled(!allSatisfied);
    }, [groupCounts, modifierGroups]);

    const toggleModifier = (modifier, group, checked) => {
        if (!checked && isMandatoryGroup(group)) return;
        setSelectedModifiers((prev) => {
            const currentCount = groupCounts[group.itemGroupId] || 0;
            const maxAllowed = group.restrictions.maxQuantity;

            if (checked) {
                if (currentCount >= maxAllowed) return prev;
                setGroupCounts(prevCounts => ({
                    ...prevCounts,
                    [group.itemGroupId]: currentCount + 1
                }));
                return [...prev, modifier];
            } else {
                setGroupCounts(prevCounts => ({
                    ...prevCounts,
                    [group.itemGroupId]: Math.max((prevCounts[group.itemGroupId] || 0) - 1, 0)

                }));
                return prev.filter(m => m.productId !== modifier.productId);
            }
        });
    };

    const getTranslated = (id, field, fallback) => {
        const translated = translations.productsById?.[id]?.[field];
        if (translated) return translated;
        
        if (field === 'name' && fallback) {
            if (hasMultiLanguageFormat(fallback)) {
                return parseMultiLanguageDescription(fallback, locale);
            }
            const menuTranslation = translations.menu?.[fallback];
            if (menuTranslation) return menuTranslation;

            if (fallback.toUpperCase() === 'STILL WATER') {
                const stillWaterTranslation = translations.menu?.['STILL WATER'];
                if (stillWaterTranslation) return stillWaterTranslation;
            }
            
            if (fallback.toUpperCase() === 'BEAN') {
                const beanTranslations = {
                    'en': 'BEAN',
                    'ru': 'ФАСОЛЬ',
                    'ge': 'ლობиო',
                    'it': 'FAGIOLO',
                    'tr': 'FASULYE',
                    'ar': 'فاصوليا'
                };
                return beanTranslations[locale] || fallback;
            }
        }
        
        if (field === 'description' && fallback) {
            if (hasMultiLanguageFormat(fallback)) {
                return parseMultiLanguageDescription(fallback, locale);
            }
            const menuTranslation = translations.menu?.[fallback];
            if (menuTranslation) return menuTranslation;
            
            if (locale !== 'en') {
                return smartTranslateIngredients(fallback, locale);
            }
        }
        
        return fallback;
    };

    const isModifierDisabled = (group, itemId) => {
        const maxAllowed = group.restrictions?.maxQuantity ?? Infinity;

        const groupSelected = selectedModifiers.filter(
            (m) => m.productGroupId === group.itemGroupId
        );

        const alreadySelected = groupSelected.some(
            (m) => m.productId === itemId
        );

        return !alreadySelected && groupSelected.length >= maxAllowed;
    };



    return (
        <div className="modifiersmodal-overlay">
            <div className="modifiersmodal-content">
                <h2 className="modifiersmodal-title">{getTranslated(product.itemId, 'name', product.name)}</h2>
                <div className="modifiersmodal-scrollable">
                    {modifierGroups.map((group) => (
                        <div key={group.itemGroupId} className="modifiersmodal-group">
                            <h3 className="modifiersmodal-group-title">
                                {getTranslated(group.itemGroupId, 'name', group.name)}
                                <span className="modifiersmodal-restrictions">
        &nbsp;({locale === 'ru' ? 'от' : locale === 'ge' ? '-დან' : 'from'} {group.restrictions.minQuantity} {locale === 'ru' ? 'до' : locale === 'ge' ? '-მდე' : 'to'} {group.restrictions.maxQuantity})
                                    {group.restrictions.minQuantity > 0 && (
                                        <span className="modifiersmodal-required-min">
                &nbsp;— {locale === 'ru' ? 'обязательно' : locale === 'ge' ? 'სავალდებულო' : 'required'} ({groupCounts[group.itemGroupId] || 0}/{group.restrictions.minQuantity})
            </span>
                                    )}
    </span>
                            </h3>

                            <div className="modifiersmodal-options">
                                {group.items.map((item) => {
                                    const modifier = {
                                        productId: item.itemId,
                                        amount: 1,
                                        productGroupId: group.itemGroupId,
                                        price: item.prices?.[0]?.price || 0,
                                    };

                                    const isMandatory = isMandatoryGroup(group);
                                    const isChecked = selectedModifiers.some(m => m.productId === item.itemId);
                                    const isDisabled = (isMandatory && isChecked) || isModifierDisabled(group, item.itemId);
                                    return (
                                        <label
                                            key={item.itemId}
                                            className={`modifiersmodal-option ${isDisabled ? 'disabled' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                disabled={isDisabled}
                                                onChange={(e) =>
                                                    toggleModifier(modifier, group, e.target.checked)
                                                }
                                            />
                                            {getTranslated(item.itemId, 'name', item.name)} (+{modifier.price}₾)
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                {isAddDisabled && (
                    <div className="modifiersmodal-warning">
                        {locale === 'ru' ? 'Пожалуйста, выберите обязательные модификаторы' : 
                         locale === 'ge' ? 'გთხოვთ, აირჩიოთ სავალდებულო მოდიფიკატორები' : 
                         'Please, choose required modifiers'}
                    </div>
                )}

                <div className="modifiersmodal-buttons">

                    <button className="btn cancel" onClick={onClose}>
                        {locale === 'ru' ? 'Отмена' : locale === 'ge' ? 'გაუქმება' : 'Cancel'}
                    </button>
                    <button
                        className="btn confirm"
                        onClick={() => {
                            const resultProduct = {...product, selectedModifiers};
                            onConfirm(resultProduct);
                        }}
                        disabled={isAddDisabled}
                    >
                        {locale === 'ru' ? 'Добавить' : locale === 'ge' ? 'დამატება' : 'Add'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModifierModal;
